/* PDP — Complete the look.
   Paired with sections/product-complete-the-look.liquid.

   Four jobs:
   1. Keep the running total and the two labels that quote a count in step
      with the tick boxes and the shade selects.
   2. Add every ticked item in one /cart/add.js post.
   3. Keep the anchor card — the product being viewed — in step with the
      variant picker above the fold: its price, the shade named under it, and
      whether it can be ticked at all. The theme dispatches
      `product:variant-change` on `document` with the variant in
      `event.detail` — see assets/theme.js and assets/custom-events.js.
   4. Add the matching kit from its own button.

   Written in ES5 with no dependencies, matching assets/product-how-to-use.js,
   assets/product-before-after.js and assets/quantity-breaks.js: this loads on
   every PDP and must not need a build step or a polyfill. */
(function () {
  'use strict';

  /* The section ships its own <script> tag, so a page carrying two of these
     loads — and runs — this file twice, binding the document listeners below
     twice with it. initAll() already walks every widget on the page, so one
     execution serves them all. Same guard as assets/product-how-to-use.js. */
  if (window.productCompleteTheLookInitialized) return;
  window.productCompleteTheLookInitialized = true;

  function find(root, selector) {
    return root.querySelector(selector);
  }

  function findAll(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  /* Only the parts belonging to this widget, never a nested one: a page can
     carry two of these sections. */
  function own(widget, selector) {
    return findAll(widget, selector).filter(function (node) {
      return node.closest('[data-ctl]') === widget;
    });
  }

  function attr(widget, name, fallback) {
    var value = widget.getAttribute('data-ctl-' + name);
    return value === null || value === '' ? fallback : value;
  }

  /* ---------------------------------------------------------------- money -- */

  function withDelimiters(number, precision, thousands, decimal) {
    if (isNaN(number) || number === null) return '0';

    var parts = (number / 100.0).toFixed(precision).split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var cents = parts[1] ? decimal + parts[1] : '';

    return dollars + cents;
  }

  /* Copied verbatim from assets/quantity-breaks.js, which in turn mirrors
     formatMoney in theme.js, so the three of them and the prices Liquid
     rendered on the same page never disagree on how a price is spelled. The
     shop's format string is the one input, which is what keeps a market
     selling in another currency correct without a second opinion from this
     file. */
  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var match = /\{\{\s*(\w+)\s*\}\}/.exec(format);
    if (!match) return format;

    var value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = withDelimiters(cents, 2, ',', '.');
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  /* ----------------------------------------------------------------- cards -- */

  function cards(widget) {
    return own(widget, '[data-ctl-item]');
  }

  function isOn(card) {
    var input = find(card, '[data-ctl-toggle]');
    return !!input && input.checked && !input.disabled;
  }

  /* The variant a card would add. The anchor has neither a select nor a
     hidden input — the picker above the fold owns its shade, and that is read
     at submit time instead, so this returns null for it on purpose. */
  function cardVariantId(card) {
    var select = find(card, '[data-ctl-variant-select]');
    if (select) return select.value;
    var hidden = find(card, '[data-ctl-variant-id]');
    return hidden ? hidden.value : null;
  }

  function cardPrice(card) {
    var select = find(card, '[data-ctl-variant-select]');
    if (select) {
      var option = select.options[select.selectedIndex];
      /* The option's own price, so a nine-shade product whose shades are not
         all the same price totals correctly. */
      if (option && option.getAttribute('data-price')) {
        return parseInt(option.getAttribute('data-price'), 10) || 0;
      }
    }
    return parseInt(card.getAttribute('data-price'), 10) || 0;
  }

  /* --------------------------------------------------------------- totals -- */

  function refresh(widget) {
    var total = 0;
    var count = 0;

    cards(widget).forEach(function (card) {
      var on = isOn(card);

      /* Drives the faded, borderless state in the stylesheet. An attribute
         rather than a class so the markup reads the same as the data
         attributes around it. */
      if (on) {
        card.removeAttribute('data-ctl-off');
      } else {
        card.setAttribute('data-ctl-off', '');
      }

      if (!on) return;
      count += 1;
      total += cardPrice(card);
    });

    var totalSlot = own(widget, '[data-ctl-total]')[0];
    if (totalSlot) totalSlot.textContent = formatMoney(total);

    var countSlot = own(widget, '[data-ctl-count-label]')[0];
    if (countSlot) {
      countSlot.textContent = attr(widget, 'total-label', 'Total for [count] items')
        .replace('[count]', count);
    }

    /* That message was an answer to a press with nothing ticked, and a tick is
       the shopper answering it back. Left up over a bundle that now has
       something in it, it reads as a second, unrelated complaint. Only the
       empty-bundle one goes: a failed add is still true whatever is ticked. */
    if (count > 0) clearError(widget, 'empty');

    var button = own(widget, '[data-ctl-add]')[0];
    if (button) {
      /* Nothing ticked is not an error state to be blocked — the shopper may
         be mid-thought — so the button stays pressable and says so when
         pressed. What it must not do is keep claiming a count of zero items
         while reading "Add 0 items to bag", so the label is rebuilt here too.

         Left alone while a request is in flight: the submit handler owns the
         label until it has put it back, and writing here would strip the
         "Adding…" copy mid-post. */
      button.ctlCount = count;
      if (button.getAttribute('aria-busy') !== 'true') drawLabel(button, count);
    }
  }

  function drawLabel(button, count) {
    var slot = find(button, '[data-ctl-button-text]') || button;
    var label = button.getAttribute('data-label') || '';
    slot.textContent = label.replace('[count]', count);
  }

  /* ---------------------------------------------------------------- anchor -- */

  /* Keeps the first card in step with the variant picker above the fold: the
     price it quotes, the shade named under it, and whether it can be ticked.
     Without this a shopper who changes shade would be looking at a card
     offering the one the page was built with. */
  /* `{ "<variant id>": { price, price_formatted, title, available } }` for the
     anchor product only, rendered by Liquid. Parsed once and kept, because it
     never changes for the life of the page. */
  function anchorVariants(widget) {
    if (widget.ctlVariants !== undefined) return widget.ctlVariants;
    var slot = own(widget, '[data-ctl-anchor-variants]')[0];
    var parsed = null;
    if (slot) {
      try {
        parsed = JSON.parse(slot.textContent);
      } catch (error) {
        parsed = null;
      }
    }
    widget.ctlVariants = parsed;
    return parsed;
  }

  /* Keeps the first card in step with the variant picker above the fold: the
     price it quotes, the shade named under it, and whether it can be ticked.
     Without this a shopper who changes shade would be looking at a card
     offering the one the page was built with.

     Driven by the id alone. `product:variant-change` is dispatched on
     `document` carrying nothing but the variant — see dispatchCustomEvent in
     theme.js — so the event cannot say which picker moved, and any other
     picker open on the page reaches this listener too. The quick-view modal
     lives in layout/theme.liquid and is therefore on every PDP. Looking the
     id up in the map Liquid rendered for *this* product is what separates
     them: an id from somewhere else is not in it and the event is dropped.

     Everything shown then comes from the map rather than from the payload,
     so a foreign event cannot reprice the card even if an id were to collide,
     and the prices are the ones `money` produced server-side.

     A no-map fallback is deliberately absent: with no map there is nothing to
     verify against, and updating from an unverifiable event is the bug this
     replaced. */
  function updateAnchor(widget, variant) {
    var card = own(widget, '[data-ctl-anchor]')[0];
    if (!card || !variant) return;

    var map = anchorVariants(widget);
    var row = map && map[String(variant.id)];
    if (!row) return;

    card.setAttribute('data-price', row.price);

    var priceSlot = find(card, '[data-ctl-price]');
    if (priceSlot) {
      priceSlot.textContent = row.price_formatted || '';
      if (row.compare_formatted) {
        var was = document.createElement('s');
        was.className = 'ctl__price-was';
        was.textContent = row.compare_formatted;
        priceSlot.appendChild(was);
      }
    }

    var nameSlot = find(card, '[data-ctl-anchor-variant]');
    if (nameSlot && row.title) nameSlot.textContent = row.title;

    /* A sold-out shade cannot be added, so the card unticks itself and the
       box goes dead rather than leaving the shopper to find out at the cart.
       Re-ticking on the way back is deliberate: a shopper who lands on a
       sold-out shade and moves to one in stock should get the product being
       viewed back in the bundle, not a silently unticked first card. */
    var input = find(card, '[data-ctl-toggle]');
    if (input) {
      if (row.available === false) {
        input.checked = false;
        input.disabled = true;
      } else {
        if (input.disabled) input.checked = true;
        input.disabled = false;
      }
    }

    refresh(widget);
  }

  /* ------------------------------------------------------------ add to cart -- */

  /* The slot remembers which error it is carrying, so the "tick at least one
     item" message can be withdrawn the moment something is ticked without the
     same call also wiping a failed add the shopper has not dealt with yet. */
  function setError(widget, message, kind) {
    var slot = own(widget, '[data-ctl-error]')[0];
    if (!slot) return;
    slot.textContent = message || '';
    slot.setAttribute('data-ctl-error', message ? kind || '' : '');
  }

  function clearError(widget, kind) {
    var slot = own(widget, '[data-ctl-error]')[0];
    if (slot && slot.getAttribute('data-ctl-error') === kind) setError(widget, '');
  }

  /* The PDP's own product form — never this section's. Ours matches
     `form[action*="/cart/add"]` too, and on a page where the real form is
     missing that selector would otherwise find ours and read back the variant
     the page was rendered with instead of refusing. */
  function productForm(widget) {
    var forms = findAll(document, '[data-product-form], form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      if (!widget.contains(forms[i])) return forms[i];
    }
    return null;
  }

  /* What the anchor card would add, read at click time out of the picker
     above the fold so it is always the shade on screen. */
  function anchorVariantId(widget) {
    var form = productForm(widget);
    var input = form && find(form, '[name="id"]');
    return (input && input.value) || null;
  }

  /* The line-item properties the shopper filled in on the main form —
     engraving, a gift note. Carried onto the anchor's line only: they belong
     to that product, and copying them onto a companion would attach a note
     about a mascara to a pencil. */
  function anchorProperties(widget) {
    var form = productForm(widget);
    if (!form) return {};

    var properties = {};
    findAll(form, '[name^="properties"]').forEach(function (input) {
      if (input.type === 'checkbox' && !input.checked) return;
      var match = input.name.match(/^properties\[(.*)\]$/);
      if (match) properties[match[1]] = input.value;
    });
    return properties;
  }

  /* Every ticked card as one /cart/add.js payload. The multi-line `items`
     form is what lets the whole routine arrive as a single request — three
     sequential posts would show the shopper three cart updates, and a failure
     on the second would leave the first already added. */
  function buildItems(widget) {
    var items = [];

    cards(widget).forEach(function (card) {
      if (!isOn(card)) return;

      var isAnchor = card.hasAttribute('data-ctl-anchor');
      var id = isAnchor ? anchorVariantId(widget) : cardVariantId(card);
      if (!id) return;

      var item = { id: id, quantity: 1 };
      if (isAnchor) {
        var properties = anchorProperties(widget);
        for (var key in properties) {
          if (Object.prototype.hasOwnProperty.call(properties, key)) {
            item.properties = properties;
            break;
          }
        }
      }
      items.push(item);
    });

    return items;
  }

  /* Shared by both buttons in the section — the row's and the kit's. Takes
     the items to add and the button to drive, and owns that button's label
     and disabled state for the life of the request. */
  function post(widget, button, textSelector, items) {
    if (button.getAttribute('aria-busy') === 'true') return;

    if (!items.length) {
      setError(widget, attr(widget, 'empty-error', 'Tick at least one item to add.'), 'empty');
      return;
    }

    setError(widget, '');

    var slot = find(button, textSelector) || button;
    var busy = button.getAttribute('data-busy-label');

    /* A restore still pending from the previous add would otherwise fire part
       way through this one and put the idle label back under "Adding…". */
    if (button.ctlRestoreTimer) {
      window.clearTimeout(button.ctlRestoreTimer);
      button.ctlRestoreTimer = null;
    }

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    if (busy) slot.textContent = busy;

    /* Rebuilt from the button's own attributes rather than captured and
       replayed: the tick boxes may have moved while the request was in
       flight, and the label has to come back showing the count it is on now,
       not the one it was pressed with. */
    var restore = function () {
      button.removeAttribute('aria-busy');
      button.disabled = false;
      if (button.hasAttribute('data-ctl-add')) {
        drawLabel(button, button.ctlCount || 0);
      } else {
        slot.textContent = button.getAttribute('data-label') || '';
      }
    };

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: items })
    }).then(function (response) {
      return response.json().then(function (data) { return { ok: response.ok, data: data }; });
    }).then(function (result) {
      if (!result.ok) {
        /* Shopify's own description is already in the shopper's language. */
        restore();
        setError(widget, (result.data && result.data.description) || attr(widget, 'add-error', 'Sorry, that could not be added.'));
        return;
      }

      var done = button.getAttribute('data-done-label');
      if (done) {
        slot.textContent = done;
        button.removeAttribute('aria-busy');
        button.disabled = false;
        button.ctlRestoreTimer = window.setTimeout(function () {
          button.ctlRestoreTimer = null;
          restore();
        }, 2000);
      } else {
        restore();
      }

      /* The theme refreshes its cart count off this event, and the header
         icon is what actually opens its drawer. */
      document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
      var icon = find(document, '[data-js-cart-icon]');
      if (icon) icon.click();
    })['catch'](function () {
      restore();
      setError(widget, attr(widget, 'add-error', 'Sorry, that could not be added.'));
    });
  }

  /* ------------------------------------------------------------------ init -- */

  function init(widget) {
    if (widget.hasAttribute('data-ctl-ready')) return;
    widget.setAttribute('data-ctl-ready', 'true');

    /* Delegated, so a card re-rendered by the theme editor is picked up
       without rebinding. `change` covers the tick boxes, the selects, and the
       keyboard — space on a focused checkbox fires it just as a click
       does. */
    widget.addEventListener('change', function (event) {
      if (!event.target.closest('[data-ctl-item]')) return;
      /* Choosing a shade on a card the shopper had unticked is them changing
         their mind about it, so the tick comes back rather than the choice
         being made against a card that is not in the total. */
      if (event.target.hasAttribute('data-ctl-variant-select')) {
        var toggle = find(event.target.closest('[data-ctl-item]'), '[data-ctl-toggle]');
        if (toggle && !toggle.disabled) toggle.checked = true;
      }
      refresh(widget);
    });

    /* Bound to submit, not to the buttons' clicks: both are real submit
       buttons inside real forms, so the section still adds to the cart with
       no JavaScript, and submit is the one event that catches the click and
       the Enter key on a focused button alike. Binding click as well would
       run the add twice for every mouse press. */
    widget.addEventListener('submit', function (event) {
      var form = event.target;
      if (form.closest('[data-ctl]') !== widget) return;

      if (form.hasAttribute('data-ctl-form')) {
        var button = find(form, '[data-ctl-add]');
        if (!button) return;
        event.preventDefault();
        post(widget, button, '[data-ctl-button-text]', buildItems(widget));
        return;
      }

      if (form.hasAttribute('data-ctl-kit-form')) {
        var kitButton = find(form, '[data-ctl-kit-add]');
        var idInput = find(form, '[name="id"]');
        if (!kitButton || !idInput) return;
        event.preventDefault();
        post(widget, kitButton, '[data-ctl-kit-button-text]', [{ id: idInput.value, quantity: 1 }]);
      }
    });

    refresh(widget);
  }

  function initAll(root) {
    findAll(root || document, '[data-ctl]').forEach(init);
  }

  document.addEventListener('product:variant-change', function (event) {
    var variant = event.detail && event.detail.variant;
    if (!variant) return;
    findAll(document, '[data-ctl]').forEach(function (widget) {
      /* Only a section showing the product the picker belongs to. A page
         placement pinned to a different product has an anchor of its own that
         this event says nothing about. */
      if (widget.getAttribute('data-ctl-adds-to-cart') !== 'true') return;
      updateAnchor(widget, variant);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); sweepResync(); });
  } else {
    initAll();
    sweepResync();
  }

  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  document.addEventListener('quickview:loaded', function () { initAll(); });

  /* Coming back with the browser's Back button restores the tick boxes to how
     the shopper left them - but it does that *after* the markup is parsed and
     init has already run its one pass. So a card they had unticked returns
     unticked and fully opaque, missing the faded state that says it is out of
     the bundle, and the total and the button's count are left quoting the
     markup's defaults. Nothing fires `change`, so the only way to notice is to
     look again once the restore has happened.

     The restore can land anywhere between parsing and the load event, and
     waiting for a single late signal is what made the card sit there looking
     selected for two or three seconds before correcting itself: `pageshow`
     fires only after `load`, and load on this page is seconds away behind the
     third-party apps. So sweep instead - a handful of passes across the first
     half second, which is where every browser actually restores form state.
     refresh only reads the DOM and writes what it finds, so repeating it is
     idempotent and costs a few property reads on a handful of nodes. */
  function resyncAll() {
    findAll(document, '[data-ctl]').forEach(refresh);
  }

  function sweepResync() {
    resyncAll();
    if (window.requestAnimationFrame) window.requestAnimationFrame(resyncAll);
    [0, 60, 180, 400].forEach(function (ms) { window.setTimeout(resyncAll, ms); });
  }

  /* Still bound, as the backstop for a bfcache restore - that fires pageshow
     without re-running any of the bootstrap above. */
  window.addEventListener('pageshow', function () {
    initAll();
    sweepResync();
  });
})();
